/**
 * Centralized save-button loading state with timeout + failsafe reset.
 * Prevents infinite "Se salvează..." on mobile when async work hangs.
 */
import { showSnackbar } from './ui.js?v=83.58';
import { logger } from './logger.js?v=83.58';

const DEFAULT_SAVE_TIMEOUT_MS = 120000;

/**
 * @param {object} opts
 * @param {HTMLElement|null} opts.btn
 * @param {string} [opts.loadingText]
 * @param {string} [opts.successText]
 * @param {string} [opts.originalText]
 * @param {number} [opts.timeoutMs]
 * @param {boolean} [opts.showSuccessSnackbar]
 * @param {string} [opts.successSnackbar]
 * @param {() => Promise<void>} opts.run
 */
export async function withSaveButtonState(opts) {
  const {
    btn,
    loadingText = 'Se salvează...',
    successText = '✓ Salvează',
    originalText = btn?.textContent || 'Salvează',
    timeoutMs = DEFAULT_SAVE_TIMEOUT_MS,
    showSuccessSnackbar = false,
    successSnackbar = 'Setările au fost salvate.',
    run
  } = opts;

  const textSpan = btn?.querySelector?.('[id$="Text"]') || null;
  const usesInnerHtml = !!textSpan;

  const setBtnText = (text, withSpinner = false) => {
    if (!btn) return;
    if (usesInnerHtml && textSpan) {
      textSpan.innerHTML = withSpinner
        ? `${text}<span class="inline-spinner"></span>`
        : text;
    } else {
      btn.textContent = text;
    }
  };

  const resetBtn = () => {
    if (!btn) return;
    btn.disabled = false;
    btn.style.pointerEvents = '';
    setBtnText(originalText);
  };

  const setLoading = () => {
    if (!btn) return;
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    setBtnText(loadingText, true);
  };

  console.log('START SAVE');
  setLoading();

  const failsafe = window.setTimeout(() => {
    console.error('SAVE ERROR', new Error('save-button-failsafe-timeout'));
    resetBtn();
    showSnackbar('Salvarea a durat prea mult. Verifică conexiunea și încearcă din nou.', 'error');
  }, timeoutMs + 3000);

  try {
    await Promise.race([
      run(),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error('save-timeout')), timeoutMs);
      })
    ]);

    console.log('SAVE FINISHED');
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      setBtnText(successText);
      window.setTimeout(() => setBtnText(originalText), 2000);
    }
    if (showSuccessSnackbar) {
      showSnackbar(successSnackbar, 'success');
    }
  } catch (e) {
    console.error('SAVE ERROR', e);
    resetBtn();
    if (e?.message === 'save-timeout') {
      showSnackbar('Salvarea durează prea mult. Verifică conexiunea și încearcă din nou.', 'error');
    } else {
      const msg = e?.message && !String(e.message).includes('save-timeout')
        ? e.message
        : 'Nu am putut salva setările. Încearcă din nou.';
      showSnackbar(msg, 'error');
    }
    logger.error('[withSaveButtonState]', e);
    throw e;
  } finally {
    window.clearTimeout(failsafe);
    if (btn) {
      btn.disabled = false;
      btn.style.pointerEvents = '';
    }
  }
}
